/**
 * Chivio — Add to Cart Choreography
 *
 * Three coordinated moments fired the instant the user clicks "Add to bag":
 *   1. Particles fly along jittered cubic beziers from the ATC button to the
 *      cart icon (7 desktop, 5 mobile).
 *   2. Cart icon pulses + hairline ring breathes outward at T+520ms.
 *   3. Toast notification slides in top-right at T+240ms.
 *
 * The cart drawer NEVER opens automatically. Foxify's auto-open behaviour
 * is suppressed via a MutationObserver that force-closes any "open" state
 * during a 1.9s window after ATC click. The toast's chevron is the only
 * sanctioned path to the drawer; tapping it ends the suppression window
 * and opens the drawer manually.
 *
 * Architecture note: we trigger the animation on the *click event itself*
 * (capture phase) rather than waiting for `theme:cart:added`. The cart-add
 * network request runs in parallel — by the time Shopify responds (~500ms
 * later), the user has already seen the particles depart. That's the spec's
 * T=0 ATC click; without it the entire interaction feels half a second
 * stale.
 *
 * Spec values from "Add to Cart Choreography" v1.0:
 *   • Total runtime: 1780ms desktop · 1700ms mobile
 *   • Core feel resolves: T+900ms
 *   • Sub-2s hard ceiling
 *   • 60Hz target — only transform/opacity animated per-frame
 */

(function () {
  'use strict';

  // ─── BRAND PALETTE — sage×2, umber×2, stone×2, slate×1 ────────────────────
  // Used as a fallback when the active product has no sibling colour.
  const BRAND_PARTICLE_COLORS = [
    '#6B6C4E', '#6B6C4E', '#4D3C31', '#4D3C31',
    '#9C8B7A', '#9C8B7A', '#556065',
  ];

  // ─── PER-PRODUCT COLOUR ───────────────────────────────────────────────────
  // The active sibling swatch carries the product's colour in
  // `data-sibling-color` (set by snippets/product-siblings.liquid). When
  // present, we build a 7-particle palette around it — base, darker, lighter,
  // plus a near-black anchor. This makes the cart-add animation visually
  // match the product being added.

  function activeSiblingColour() {
    const swatch = document.querySelector('.siblings a[aria-current="true"]');
    if (!swatch) return null;
    const c = (swatch.getAttribute('data-sibling-color') || '').trim();
    return c && /^#[0-9a-f]{3,6}$/i.test(c) ? c : null;
  }

  function hexToRgb(hex) {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return null;
    return {
      r: parseInt(h.slice(0, 2), 16),
      g: parseInt(h.slice(2, 4), 16),
      b: parseInt(h.slice(4, 6), 16),
    };
  }

  function rgbToHex(r, g, b) {
    const clamp = (x) => Math.max(0, Math.min(255, Math.round(x)));
    const hx = (x) => clamp(x).toString(16).padStart(2, '0');
    return '#' + hx(r) + hx(g) + hx(b);
  }

  function shade(hex, amount) {
    // amount: -1 (towards black) … +1 (towards white)
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const t = amount > 0 ? 255 : 0;
    const a = Math.abs(amount);
    return rgbToHex(
      rgb.r + (t - rgb.r) * a,
      rgb.g + (t - rgb.g) * a,
      rgb.b + (t - rgb.b) * a
    );
  }

  function perceivedLightness(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0.5;
    // Rec. 601 luma — close enough for "is this light?"
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  }

  function buildParticleColours() {
    const base = activeSiblingColour();
    if (!base) return BRAND_PARTICLE_COLORS;

    // Light colours (cream, beige, white) get darkened so they read against
    // the linen page background. The hairline outline already adds an edge,
    // but a fully white particle on cream paper is invisible regardless.
    let primary = base;
    if (perceivedLightness(base) > 0.7) primary = shade(base, -0.4);

    return [
      primary,
      primary,
      shade(primary, -0.25),
      shade(primary, -0.25),
      shade(primary, 0.18),
      shade(primary, 0.18),
      '#1a1a1a',
    ];
  }
  // Sizes bumped from spec values [4,4,6,6,6,6,7] / [4,6,6,6,7] — at the
  // original sizes the brand-toned particles were physically invisible
  // against the linen background during a 520ms flight. Same intent (quiet,
  // restrained), just with enough visual presence to register.
  const PARTICLE_SIZES_DESKTOP = [8, 8, 10, 10, 10, 10, 12];
  const PARTICLE_SIZES_MOBILE  = [8, 10, 10, 10, 12];
  const STAGGER_DESKTOP = [0, 35, 70, 105, 140, 175, 210];
  const STAGGER_MOBILE  = [0, 50, 100, 150, 200];

  const FLIGHT_DESKTOP = 520;
  const FLIGHT_MOBILE  = 480;
  const TOAST_ENTRY_DELAY  = 240;
  const CART_PULSE_DELAY   = 520;
  const TOAST_HOLD_DESKTOP = 1100;
  const TOAST_HOLD_MOBILE  = 1000;
  const TOAST_ENTRY_MS     = 220;
  const TOAST_EXIT_MS      = 220;

  // Suppression window covers from click → toast exit. 1900ms leaves a small
  // tail after the 1780ms runtime so we never race the drawer reopening.
  const SUPPRESSION_WINDOW_MS = 1900;

  // Animation lockout — prevents the same cart-add from triggering the
  // animation twice (once on click, once when theme:cart:added fires after
  // a slow API roundtrip). Independent of suppression so it can outlast it.
  const ANIMATION_LOCKOUT_MS = 5000;
  let lastAnimationT = 0;

  const isMobile  = () => window.matchMedia('(max-width: 749px)').matches;
  const isReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ─── DOM HELPERS ──────────────────────────────────────────────────────────

  let particleLayer = null;
  function getParticleLayer() {
    if (particleLayer && document.body.contains(particleLayer)) return particleLayer;
    particleLayer = document.createElement('div');
    particleLayer.className = 'chivio-atc-particles';
    document.body.appendChild(particleLayer);
    return particleLayer;
  }

  let toastRegion = null;
  function getToastRegion() {
    if (toastRegion && document.body.contains(toastRegion)) return toastRegion;
    toastRegion = document.createElement('div');
    toastRegion.className = 'chivio-atc-toasts';
    document.body.appendChild(toastRegion);
    return toastRegion;
  }

  function center(el) {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function rand(min, max) { return min + Math.random() * (max - min); }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[m]);
  }

  // Optimistically +1 every visible cart-count badge so the count reflects
  // the add instantly, instead of waiting ~2s for the server. Foxify writes
  // the authoritative count when the request lands (same value), so there's
  // no drift on success.
  function bumpCartCountOptimistic() {
    const sel = '[data-cart-count], .cart-count, .sm-cart-count, .cart__toggle__count, .cart__count, [data-cart-item-count]';
    document.querySelectorAll(sel).forEach(function (el) {
      const raw = (el.textContent || '').replace(/[^\d]/g, '');
      if (raw === '') return;
      const n = parseInt(raw, 10);
      if (isNaN(n)) return;
      el.textContent = el.textContent.replace(/\d+/, String(n + 1));
      el.classList.remove('hidden', 'is-empty');
    });
  }

  // ─── CUBIC BEZIER ─────────────────────────────────────────────────────────

  function bezPoint(p0, p1, p2, p3, t) {
    const u = 1 - t, u2 = u * u, u3 = u2 * u, t2 = t * t, t3 = t2 * t;
    return {
      x: u3 * p0.x + 3 * u2 * t * p1.x + 3 * u * t2 * p2.x + t3 * p3.x,
      y: u3 * p0.y + 3 * u2 * t * p1.y + 3 * u * t2 * p2.y + t3 * p3.y,
    };
  }

  // Mirror of cubic-bezier(.22,.61,.36,1) — Newton-Raphson on x(t).
  function easeFlight(x) {
    const p1x = 0.22, p1y = 0.61, p2x = 0.36, p2y = 1;
    const bezX  = (t) => 3 * (1-t)*(1-t)*t*p1x + 3 * (1-t)*t*t*p2x + t*t*t;
    const bezDX = (t) => 3*(1-t)*(1-t)*p1x + 6*(1-t)*t*(p2x - p1x) + 3*t*t*(1 - p2x);
    const bezY  = (t) => 3 * (1-t)*(1-t)*t*p1y + 3 * (1-t)*t*t*p2y + t*t*t;
    let t = x;
    for (let i = 0; i < 6; i++) {
      const dx = bezX(t) - x;
      const d  = bezDX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= dx / d;
    }
    return bezY(Math.min(1, Math.max(0, t)));
  }

  // ─── PARTICLES ────────────────────────────────────────────────────────────

  function spawnParticle(idx, fromCenter, toCenter, sizes, flightMs, colours) {
    const layer = getParticleLayer();
    const size = sizes[idx % sizes.length];

    const el = document.createElement('span');
    el.className = 'chivio-atc-particle';
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.background = colours[idx % colours.length];
    layer.appendChild(el);

    const origin = {
      x: fromCenter.x + rand(-18, 18),
      y: fromCenter.y + rand(-4, 0),
    };
    const target = {
      x: toCenter.x + rand(-2, 2),
      y: toCenter.y + rand(-2, 2),
    };
    const dx = target.x - origin.x;
    const dy = target.y - origin.y;
    const cp1 = {
      x: origin.x + dx * 0.33 + rand(-60, 60),
      y: origin.y + dy * 0.33 - rand(80, 180),
    };
    const cp2 = {
      x: origin.x + dx * 0.75 + rand(-30, 30),
      y: origin.y + dy * 0.75 - rand(30, 110),
    };

    const start = performance.now();
    const half = size / 2;

    function frame(now) {
      const elapsed = now - start;
      const tRaw = Math.min(1, elapsed / flightMs);
      const tEased = easeFlight(tRaw);
      const pt = bezPoint(origin, cp1, cp2, target, tEased);

      let op = 1;
      if (elapsed < 60) op = elapsed / 60;
      else if (elapsed > flightMs - 120) op = Math.max(0, (flightMs - elapsed) / 120);

      el.style.transform = 'translate3d(' + (pt.x - half) + 'px,' + (pt.y - half) + 'px,0)';
      el.style.opacity = op;

      if (tRaw < 1) {
        requestAnimationFrame(frame);
      } else {
        el.remove();
      }
    }
    requestAnimationFrame(frame);
    // Failsafe: if rAF stalls (tab backgrounded mid-flight, etc.) the frame
    // loop may never reach tRaw>=1, leaving a stray particle dot stuck on
    // screen. Guarantee removal shortly after the flight should have ended.
    setTimeout(function () { el.remove(); }, flightMs + 600);
  }

  // ─── CART ICON PULSE + RING ───────────────────────────────────────────────

  function pulseCart(cartIcon, reduced) {
    if (!cartIcon) return;

    const old = cartIcon.querySelector('.chivio-cart-ring');
    if (old) old.remove();

    if (reduced) {
      const svg = cartIcon.querySelector('svg');
      if (svg) {
        svg.classList.remove('chivio-cart-svg-pulse--reduced');
        void svg.offsetWidth;
        svg.classList.add('chivio-cart-svg-pulse--reduced');
        setTimeout(() => svg.classList.remove('chivio-cart-svg-pulse--reduced'), 200);
      }
      const ring = document.createElement('span');
      ring.className = 'chivio-cart-ring chivio-cart-ring--reduced';
      cartIcon.appendChild(ring);
      setTimeout(() => ring.remove(), 220);
      return;
    }

    const svg = cartIcon.querySelector('svg');
    if (svg) {
      svg.classList.remove('chivio-cart-svg-pulse');
      void svg.offsetWidth;
      svg.classList.add('chivio-cart-svg-pulse');
      setTimeout(() => svg.classList.remove('chivio-cart-svg-pulse'), 400);
    }
    const ring = document.createElement('span');
    ring.className = 'chivio-cart-ring';
    cartIcon.appendChild(ring);
    setTimeout(() => ring.remove(), 460);
  }

  // ─── TOAST ────────────────────────────────────────────────────────────────

  function showToast(detail) {
    const region = getToastRegion();

    const existing = region.querySelectorAll('.chivio-toast');
    if (existing.length >= 3) existing[0].remove();

    const t = document.createElement('div');
    t.className = 'chivio-toast';
    t.innerHTML =
      '<div class="chivio-toast__thumb">' +
        (detail.image ? '<img src="' + escapeHtml(detail.image) + '" alt=""/>' : '') +
      '</div>' +
      '<div class="chivio-toast__body">' +
        '<div class="chivio-toast__eyebrow">Added to bag</div>' +
        '<div class="chivio-toast__name">' +
          escapeHtml(detail.name || 'Item') +
          (detail.size ? ' · ' + escapeHtml(detail.size) : '') +
        '</div>' +
      '</div>' +
      '<button class="chivio-toast__cta" type="button" aria-label="Open cart drawer">' +
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">' +
          '<path d="M9 6l6 6-6 6"/>' +
        '</svg>' +
      '</button>';
    region.appendChild(t);

    // CLIENT REQUEST: the entire toast is the tap target — clicking
    // anywhere on the black widget opens the cart (not just the chevron).
    function goToCart(e) {
      if (e) e.stopPropagation();
      stopSuppression();
      openCartDrawer();
      dismissToast(t);
    }
    t.style.cursor = 'pointer';
    t.addEventListener('click', goToCart);

    const holdMs = isMobile() ? TOAST_HOLD_MOBILE : TOAST_HOLD_DESKTOP;
    let dismissTimer = null;

    function scheduleDismiss() {
      clearTimeout(dismissTimer);
      dismissTimer = setTimeout(() => dismissToast(t), holdMs);
    }

    // Pause auto-dismiss on hover ONLY for real mouse pointers. On touch a
    // tap fires `mouseenter` with no matching `mouseleave`, which used to
    // clear the timer permanently — the toast then stayed forever and, with
    // pointer-events:auto, blocked every button under it. (The client's
    // "it doesn't go away and makes other buttons unusable" bug.)
    if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
      t.addEventListener('mouseenter', () => clearTimeout(dismissTimer));
      t.addEventListener('mouseleave', scheduleDismiss);
    }

    // Absolute failsafe: the toast can NEVER outlive this, regardless of
    // hover/timer state — guarantees it self-removes and stops blocking UI.
    const TOAST_FAILSAFE_MS = 6000;
    setTimeout(() => dismissToast(t), TOAST_FAILSAFE_MS);

    requestAnimationFrame(() => {
      t.classList.add('is-in');
      setTimeout(scheduleDismiss, TOAST_ENTRY_MS);
    });
  }

  function dismissToast(t) {
    if (!t || t.classList.contains('is-out')) return;
    t.classList.remove('is-in');
    t.classList.add('is-out');
    setTimeout(() => t.remove(), TOAST_EXIT_MS + 60);
  }

  // ─── CART DRAWER SUPPRESSION ──────────────────────────────────────────────
  // Foxify's "cart drawer" is actually <cart-element id="cart-drawer">, and
  // it opens via the body class `js-drawer-open-cart` (per cart-drawer.css:350).
  // We suppress by removing that class the instant it gets added, during a
  // 1.9s window after the ATC click.
  //
  // We also defend against any other "open" classes Foxify might add to the
  // drawer element itself, in case the implementation varies.

  let suppressionActive = false;
  let suppressionTimer = null;

  // The actual class used by Foxify to open the cart drawer
  const CART_OPEN_BODY_CLASS = 'js-drawer-open-cart';

  // Other body lock classes that should also be cleared
  const ALL_BODY_LOCK_CLASSES = [
    'js-drawer-open-cart',
    'js-drawer-open',
    'drawer-open',
    'overlay-open',
    'lock',
    'cart-drawer-open',
    'no-scroll',
    'has-drawer-open',
    'is-locked',
  ];

  // Drawer-element-level open patterns (defensive)
  const DRAWER_OPEN_CLASS_PATTERNS = [
    'is-open', 'open',
    'drawer--active', 'drawer-active', 'drawer__open',
    'cart-drawer--active', 'cart-drawer-active',
    'cart-drawer--open', 'cart-drawer-open',
  ];

  function getDrawerEl() {
    return document.getElementById('cart-drawer') ||
           document.querySelector('[data-cart-drawer]') ||
           document.querySelector('cart-element#cart-drawer');
  }

  function isAnyDrawerOpen() {
    if (document.body.classList.contains(CART_OPEN_BODY_CLASS)) return true;
    const drawer = getDrawerEl();
    if (!drawer) return false;
    for (let i = 0; i < DRAWER_OPEN_CLASS_PATTERNS.length; i++) {
      if (drawer.classList.contains(DRAWER_OPEN_CLASS_PATTERNS[i])) return true;
    }
    if (drawer.getAttribute('aria-hidden') === 'false') return true;
    if (drawer.getAttribute('aria-expanded') === 'true') return true;
    return false;
  }

  function forceCloseDrawer() {
    const drawer = getDrawerEl();

    // Move focus out of the drawer BEFORE setting aria-hidden, otherwise the
    // browser logs an accessibility error (focus trapped inside aria-hidden).
    // Foxify auto-focuses the close button when the drawer opens.
    if (drawer && drawer.contains(document.activeElement)) {
      try { document.activeElement.blur(); } catch (e) { /* no-op */ }
      document.body.focus({ preventScroll: true });
    }

    // Clear body lock classes (this is the primary mechanism in Foxify)
    ALL_BODY_LOCK_CLASSES.forEach(c => {
      document.body.classList.remove(c);
      document.documentElement.classList.remove(c);
    });
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    if (drawer) {
      DRAWER_OPEN_CLASS_PATTERNS.forEach(c => drawer.classList.remove(c));
      drawer.setAttribute('aria-hidden', 'true');
      drawer.setAttribute('aria-expanded', 'false');
      // Sync Foxify's internal flag directly. Do NOT call closeCartDrawer()
      // here: it interleaves with the in-flight add routine and leaves the
      // ATC button permanently disabled in its is-loading state. Stripping
      // classes without syncing this flag desyncs the toggle instead (next
      // open needs two taps) — the bare property write avoids both.
      try { drawer.isCartDrawerOpen = false; } catch (e) { /* no-op */ }
    }

    // CRITICAL: Foxify dispatched `theme:scroll:lock` when it opened the
    // drawer (scroll-lock library + data-scroll-locked on <html>). Removing
    // the body classes above does NOT undo that — without an explicit unlock
    // the page stays frozen after add-to-cart.
    //
    // RACE: Foxify's lock handler applies the lock inside a setTimeout, so
    // an unlock dispatched synchronously here can run BEFORE the deferred
    // lock lands — the lock then wins and the page is stuck. Dispatch now,
    // again next tick, and sweep once more after the dust settles.
    dispatchScrollUnlock();
    setTimeout(dispatchScrollUnlock, 30);
    setTimeout(function () {
      if (!isAnyDrawerOpen() && document.documentElement.hasAttribute('data-scroll-locked')) {
        dispatchScrollUnlock();
      }
    }, 200);
  }

  function dispatchScrollUnlock() {
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  function setupSuppressionObservers() {
    function check() {
      if (!suppressionActive) return;
      if (isAnyDrawerOpen()) forceCloseDrawer();
    }

    // Body class changes — primary signal for Foxify
    const bodyObs = new MutationObserver(check);
    bodyObs.observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Drawer element changes — defensive
    const drawer = getDrawerEl();
    if (drawer) {
      const drawerObs = new MutationObserver(check);
      drawerObs.observe(drawer, {
        attributes: true,
        attributeFilter: ['class', 'open', 'aria-hidden', 'aria-expanded'],
      });
    }

    // <html> too — some themes lock at root
    const htmlObs = new MutationObserver(check);
    htmlObs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    // Drawer might mount late
    const tree = new MutationObserver(() => {
      if (suppressionActive) check();
    });
    tree.observe(document.body, { childList: true, subtree: true });
  }

  function startSuppression() {
    suppressionActive = true;

    // Immediate sweep: kill anything already open
    forceCloseDrawer();

    // Backstop: 50ms in, sweep again in case of asynchronous open
    setTimeout(() => {
      if (suppressionActive && isAnyDrawerOpen()) forceCloseDrawer();
    }, 50);
    setTimeout(() => {
      if (suppressionActive && isAnyDrawerOpen()) forceCloseDrawer();
    }, 250);

    clearTimeout(suppressionTimer);
    suppressionTimer = setTimeout(stopSuppression, SUPPRESSION_WINDOW_MS);
  }

  function stopSuppression() {
    suppressionActive = false;
    clearTimeout(suppressionTimer);
    suppressionTimer = null;
    // Final safety net: if the suppression window ends with the drawer
    // closed but the scroll lock still engaged (lost race), release it.
    setTimeout(function () {
      if (!isAnyDrawerOpen() && document.documentElement.hasAttribute('data-scroll-locked')) {
        dispatchScrollUnlock();
      }
    }, 60);
  }

  // Open the cart drawer (the sidebar). Used when the user clicks the
  // cart icon or the toast CTA — explicit user intent, not the implicit
  // post-add auto-open which we still suppress. */
  function openCartDrawer() {
    stopSuppression();
    const drawer = getDrawerEl();
    // Prefer Foxify's own API — keeps isCartDrawerOpen in sync so the
    // close button works on the first tap.
    if (drawer && typeof drawer.openCartDrawer === 'function') {
      try { drawer.openCartDrawer(); return; } catch (e) { /* fall through */ }
    }
    document.body.classList.add('js-drawer-open-cart');
    if (drawer) {
      drawer.setAttribute('aria-hidden', 'false');
      drawer.setAttribute('aria-expanded', 'true');
    }
  }

  // ─── PRODUCT INFO GATHERING ───────────────────────────────────────────────

  function findProductInfo() {
    const all = document.querySelectorAll('product-info[id^="MainProduct-"]');
    for (let i = 0; i < all.length; i++) {
      const style = all[i].getAttribute('style') || '';
      if (style.indexOf('display: none') === -1 && style.indexOf('display:none') === -1) {
        return all[i];
      }
    }
    return all[0] || null;
  }

  function gatherFromDom() {
    const info = findProductInfo();
    const scope = info || document;

    // Title: <h1 class="product__title"> in Foxify (lives outside <product-info>
    // in some templates, so fall back to document scope if needed).
    let titleEl = scope.querySelector('.product__title, .product-title');
    if (!titleEl) titleEl = document.querySelector('.product__title, .product-title');
    if (!titleEl && info) titleEl = info.querySelector('h1');
    if (!titleEl) titleEl = document.querySelector('main h1, [class*="product"] h1, h1');
    let name = titleEl ? titleEl.textContent.trim() : '';
    if (!name) {
      // og:title meta is always present on PDPs and carries the product name
      const og = document.querySelector('meta[property="og:title"]');
      if (og) name = (og.getAttribute('content') || '').split('–')[0].trim();
    }

    // Selected size (radio input)
    let size = '';
    const checked = scope.querySelector(
      'input[type="radio"][name*="ize" i]:checked, input[type="radio"][name*="Size"]:checked'
    ) || document.querySelector(
      'input[type="radio"][name*="ize" i]:checked'
    );
    if (checked) {
      size = checked.value || '';
      if (!size && checked.id) {
        const lbl = document.querySelector('label[for="' + checked.id + '"]');
        if (lbl) size = lbl.textContent.trim();
      }
    }

    // Featured/active gallery image
    let image = '';
    const galleryImg = scope.querySelector(
      '.product-gallery img, .product__media img, [class*="gallery"] img'
    ) || document.querySelector('.product-gallery img, .product__media img');
    if (galleryImg) image = galleryImg.currentSrc || galleryImg.src || '';

    return { name, size, image };
  }

  // ─── ATC / CART ICON FINDERS ──────────────────────────────────────────────

  const ATC_BUTTON_SELECTOR =
    'product-form button[type="submit"], ' +
    '.product-form__submit, ' +
    '[data-add-to-cart], ' +
    'button[name="add"], ' +
    // Mobile sticky cart-bar ATC is type="button" with this class — without
    // it the choreography never fires on mobile (snippets/cart-bar.liquid).
    '.product__submit__add, ' +
    '.cart-bar .btn--submit';

  // Foxify renders both the desktop and mobile cart toggles in the DOM, with
  // the inactive one display:none'd via CSS at the active breakpoint. The
  // inactive one still matches `.cart__toggle` selectors but its bounding
  // rect is all zeros — which silently aimed our particles at (0, 0) — the
  // top-left corner — instead of the real, visible cart icon.
  //
  // Always pick the first cart toggle that's actually rendered with size.
  // We also include the new staggered menu's .sm-cart since the original
  // .cart__toggle is now hidden (Foxify header is suppressed when the
  // staggered menu mounts).
  function findCartIcon() {
    const candidates = document.querySelectorAll('.sm-cart, .cart__toggle, [data-cart-toggle]');
    let best = null;
    let bestRight = -Infinity;
    for (let i = 0; i < candidates.length; i++) {
      const el = candidates[i];
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) continue;             // zero-size = hidden
      if (r.bottom < 0 || r.top > window.innerHeight) continue; // off-screen
      // Also skip if positioned to the LEFT of the viewport center —
      // the actual header cart icon is always top-right. Mobile menu
      // copies of the cart toggle live in slide-in panels at x≈0.
      if (r.right < window.innerWidth * 0.45) continue;
      // Prefer the icon nearest the top of the viewport
      if (r.top > 200) continue;
      const cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') continue;
      if (parseFloat(cs.opacity) === 0) continue;
      // Pick the rightmost qualifying icon
      if (r.right > bestRight) {
        best = el;
        bestRight = r.right;
      }
    }
    if (best) return best;
    // Last-resort fallback: any visible candidate
    for (let i = 0; i < candidates.length; i++) {
      const r = candidates[i].getBoundingClientRect();
      if (r.width > 4 && r.height > 4) return candidates[i];
    }
    return candidates[0] || null;
  }

  // ─── MAIN CHOREOGRAPHY ────────────────────────────────────────────────────

  function runChoreography(fromCenter, detail) {
    const cartIcon = findCartIcon();

    // Mark the animation as having run so the cart:added backup handler
    // doesn't double-fire it on slow networks.
    lastAnimationT = performance.now();

    // No usable cart icon (hidden header, scrolled-away non-sticky header on
    // mobile, etc.) — the toast is still the confirmation the user needs.
    // Skip only the particles + icon pulse, never the whole choreography.
    if (!cartIcon) {
      console.warn('[chivio-atc] cart icon not found — showing toast only');
      setTimeout(() => showToast(detail), TOAST_ENTRY_DELAY);
      if (isMobile() && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(8); } catch (e) { /* no-op */ }
      }
      return;
    }

    if (!fromCenter) {
      const fallback = document.querySelector(ATC_BUTTON_SELECTOR);
      fromCenter = fallback ? center(fallback) : { x: window.innerWidth / 2, y: window.innerHeight - 80 };
    }

    const toCenter = center(cartIcon);
    const reduced = isReduced();

    if (reduced) {
      pulseCart(cartIcon, true);
      setTimeout(() => showToast(detail), 0);
      return;
    }

    const sizes   = isMobile() ? PARTICLE_SIZES_MOBILE  : PARTICLE_SIZES_DESKTOP;
    const stagger = isMobile() ? STAGGER_MOBILE         : STAGGER_DESKTOP;
    const flight  = isMobile() ? FLIGHT_MOBILE          : FLIGHT_DESKTOP;

    // Palette is computed once per animation run so all particles in this
    // burst share a coherent colour story.
    const colours = buildParticleColours();

    stagger.forEach((delay, idx) => {
      setTimeout(() => spawnParticle(idx, fromCenter, toCenter, sizes, flight, colours), delay);
    });

    setTimeout(() => showToast(detail), TOAST_ENTRY_DELAY);
    setTimeout(() => pulseCart(cartIcon, false), CART_PULSE_DELAY);

    if (isMobile() && typeof navigator.vibrate === 'function') {
      try { navigator.vibrate(8); } catch (e) { /* no-op */ }
    }
  }

  // ─── INIT ─────────────────────────────────────────────────────────────────

  function init() {
    setupSuppressionObservers();

    // An explicit click on any cart toggle is user intent to open the
    // drawer — end the suppression window immediately so the drawer
    // doesn't snap shut mid-animation when opened right after an ATC.
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('a[href*="/cart"], .cart__toggle, [data-cart-toggle], [data-cart-drawer-toggle], .sm-cart')) {
        stopSuppression();
      }
    }, true);

    // Removed explicit cart navigation interceptor so the theme's native cart drawer can open

    // PRIMARY TRIGGER — ATC click. Captured before form submission so we
    // animate the same instant the user clicks, not after the API roundtrip.
    document.addEventListener('click', function (e) {
      const btn = e.target.closest(ATC_BUTTON_SELECTOR);
      if (!btn) return;

      // RE-CLICK GUARD — because we re-enable the button early (below) for
      // snappiness, block a second add on the SAME button within 1.5s so a
      // double-tap can't add two. Legitimate later adds (>1.5s) pass through.
      const now = performance.now();
      const lastTs = parseFloat(btn.getAttribute('data-chivio-add-ts') || '0');
      if (now - lastTs < 1500) { e.preventDefault(); e.stopPropagation(); return; }
      if (btn.disabled) return; // genuinely disabled (e.g. sold out)
      btn.setAttribute('data-chivio-add-ts', String(now));

      const r = btn.getBoundingClientRect();
      const fromCenter = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      const detail = gatherFromDom();

      startSuppression();
      runChoreography(fromCenter, detail);

      // PERCEIVED SPEED — the server /cart/add on this store can take ~2s
      // (an app hooks cart-add), during which Foxify keeps the button in its
      // is-loading spinner + disabled. The "Added to bag" toast confirms the
      // instant the user clicks, so we FULLY re-enable the button shortly
      // after (spinner off + clickable again) instead of waiting ~3.4s. The
      // re-click guard above prevents accidental double-adds. We re-enable
      // again after the request should have landed, as a safety net.
      bumpCartCountOptimistic();
      function reenable() {
        if (!btn.isConnected) return;
        btn.classList.remove('is-loading');
        btn.disabled = false;
      }
      setTimeout(reenable, 550);
      setTimeout(reenable, 2600);

      // WATCHDOG — last-resort reset if anything left it stuck.
      setTimeout(function () {
        if (btn.isConnected && btn.disabled) {
          btn.classList.remove('is-loading');
          btn.disabled = false;
        }
      }, 6000);
    }, true);

    // BACKUP TRIGGER — theme:cart:added. Catches add-to-cart from quick view,
    // recommended-products carousels, etc. where we didn't see the click.
    // Lockout (5s) prevents the slow-API double-trigger: click handler
    // already ran the animation, then 1.5s later cart:added arrives and
    // would otherwise re-run it.
    //
    // Foxify's `theme:cart:added` event also fires on quantity decreases and
    // line-item removals — its name is misleading, it's really "cart
    // updated". Skip the animation entirely on the /cart page where users
    // are adjusting their existing cart, never adding new items via this UI.
    document.addEventListener('theme:cart:added', function (e) {
      if (document.body.classList.contains('template-cart')) return;
      if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) return;
      if (isAnyDrawerOpen()) return;

      if (performance.now() - lastAnimationT < ANIMATION_LOCKOUT_MS) {
        // Already animated this add — just keep the drawer suppressed
        startSuppression();
        return;
      }

      const dom = gatherFromDom();
      const evd = (e && e.detail) || {};
      const detail = {
        name:  dom.name  || evd.product_title || evd.title || 'Item',
        size:  dom.size  || evd.variant_title || '',
        image: dom.image || evd.image || '',
      };
      startSuppression();
      runChoreography(null, detail);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Debug API — manual test from DevTools without a real cart-add
  window.chivioAtcDebug = {
    run: runChoreography,
    openCart: openCartDrawer,
    suppression: () => ({ active: suppressionActive }),
    test: function () {
      const btn = document.querySelector(ATC_BUTTON_SELECTOR);
      const fromCenter = btn
        ? (() => { const r = btn.getBoundingClientRect(); return { x: r.left + r.width / 2, y: r.top + r.height / 2 }; })()
        : { x: window.innerWidth / 2, y: window.innerHeight - 80 };
      runChoreography(fromCenter, {
        name: 'Classic crew tee',
        size: 'M',
        image: document.querySelector('.product-gallery img, .product__media img')?.src || '',
      });
    },
    // Diagnostic: drop 8 huge bright-red static dots across the screen for 3s.
    // If you can't see THESE, the particle layer itself is being hidden by
    // some Foxify CSS/stacking context, not subtlety.
    testLoud: function () {
      const layer = getParticleLayer();
      const w = window.innerWidth, h = window.innerHeight;
      for (let i = 0; i < 8; i++) {
        const el = document.createElement('span');
        el.className = 'chivio-atc-particle';
        el.style.width = '50px';
        el.style.height = '50px';
        el.style.background = '#FF0033';
        el.style.opacity = '1';
        el.style.boxShadow = '0 0 20px rgba(255,0,51,0.6)';
        el.style.transform = 'translate3d(' + (60 + (w - 120) * i / 7) + 'px,' + (h * 0.4) + 'px,0)';
        layer.appendChild(el);
        setTimeout(() => el.remove(), 3000);
      }
      console.log('[chivio-atc] testLoud: 8 red dots placed across viewport for 3s');
    },
  };
})();
